import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { clearImpersonation, setSession } from "../utils/session";
import { UserContext } from "../components/UserContext";
import AuroraBackground from "../components/AuroraBackground";
import { useTranslate } from "../i18n";

const inputStyle = {
  background: "var(--input-bg)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "10px 13px",
  color: "var(--text)",
  fontSize: 13,
  outline: "none",
  fontFamily: "inherit",
  width: "100%",
  transition: "border-color 0.15s",
};

function Login() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await api.post("/api/v1/users/login", {
        email: formData.email,
        password: formData.password,
      });
      if (response.status === 200) {
        const { user, session } = response.data.data;
        clearImpersonation();
        setSession(session);
        setUser(user);
        navigate("/");
      }
    } catch (err) {
      setError(t("auth.login.error.invalidCredentials"));
    }
    setIsLoading(false);
  };

  return (
    <div
      className="login-screen"
      style={{
        minHeight: "100vh",
        display: "flex",
        overflow: "hidden",
        position: "relative",
        background: "var(--bg)",
      }}
    >
      {/* Background image */}
      <div
        className="login-bg-image"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/jpg/background.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.52,
          pointerEvents: "none",
        }}
      />
      <AuroraBackground intensity={0.6} />

      {/* Left — branding */}
      <div
        className="hidden md:flex flex-1 flex-col justify-between"
        style={{ padding: "48px 64px", position: "relative", zIndex: 1 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src={`${process.env.PUBLIC_URL}/assets/svg/eclipse.svg`}
            alt={t("shared.brand.logoAlt")}
            style={{ width: 28, height: 28 }}
          />
          <span
            style={{
              fontWeight: 700,
              fontSize: 16,
              letterSpacing: "0.08em",
              color: "var(--text)",
            }}
          >
            {t("shared.brand.name")}
          </span>
          <span
            style={{
              fontSize: 11,
              color: "#fff",
              marginLeft: 4,
              opacity: 0.85,
            }}
          >
            {t("auth.shared.byline")}
          </span>
        </div>
        <div>
          <p
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.12em",
              color: "var(--muted)",
              textTransform: "uppercase",
              marginBottom: 16,
            }}
          >
            {t("auth.login.hero.eyebrow")}
          </p>
          <h2
            style={{
              fontSize: 42,
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: "var(--text)",
              marginBottom: 16,
            }}
          >
            {t("auth.login.hero.titleLine1")}
            <br />
            {t("auth.login.hero.titleLine2")}
          </h2>
          <p
            style={{
              color: "var(--muted)",
              fontSize: 14,
              maxWidth: 380,
              lineHeight: 1.7,
            }}
          >
            {t("auth.login.hero.description")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 24 }}>
          {[
            t("auth.shared.feature.multilingual"),
            t("auth.shared.feature.multitenant"),
            t("auth.shared.feature.roles"),
          ].map((f) => (
            <div
              key={f}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: "var(--muted)",
                fontSize: 12,
              }}
            >
              <span style={{ color: "var(--accent)", fontSize: 10 }}>●</span>
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Right — form */}
      <div
        className="flex items-center justify-center w-full md:w-[460px] p-6 md:p-12"
        style={{ position: "relative", zIndex: 1 }}
      >
        <form
          onSubmit={handleLogin}
          className="glass-lg login-card"
          style={{ width: "100%", borderRadius: 20, padding: "44px 38px" }}
        >
          <div style={{ marginBottom: 32 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 24,
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/svg/eclipse.svg`}
                alt={t("shared.brand.logoAlt")}
                style={{ width: 24, height: 24 }}
              />
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  letterSpacing: "0.08em",
                  color: "var(--text)",
                }}
              >
                {t("shared.brand.name")}
              </span>
            </div>
            <h3
              style={{
                fontSize: 22,
                fontWeight: 700,
                marginBottom: 8,
                letterSpacing: "-0.02em",
                color: "var(--text)",
              }}
            >
              {t("auth.login.form.title")}
            </h3>
            <p style={{ color: "var(--muted)", fontSize: 13 }}>
              {t("auth.login.form.description")}
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--muted2)",
                }}
              >
                {t("auth.shared.emailLabel")}{" "}
                <span style={{ color: "var(--danger)" }}>*</span>
              </label>
              <input
                type="email"
                name="email"
                placeholder={t("auth.shared.emailPlaceholder")}
                value={formData.email}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={(e) =>
                  (e.target.style.borderColor = "var(--accent-focus)")
                }
                onBlur={(e) => (e.target.style.borderColor = "var(--border)")}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--muted2)",
                }}
              >
                {t("auth.shared.passwordLabel")}{" "}
                <span style={{ color: "var(--danger)" }}>*</span>
              </label>
              <input
                type="password"
                name="password"
                placeholder={t("auth.shared.passwordPlaceholder")}
                value={formData.password}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={(e) =>
                  (e.target.style.borderColor = "var(--accent-focus)")
                }
                onBlur={(e) => (e.target.style.borderColor = "var(--border)")}
              />
            </div>
          </div>

          {error && (
            <p style={{ color: "var(--danger)", fontSize: 12, marginTop: 12 }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: "100%",
              marginTop: 22,
              padding: "12px 0",
              borderRadius: 9,
              background: "var(--accent)",
              color: "#fff",
              border: "none",
              fontWeight: 600,
              fontSize: 14,
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.7 : 1,
              fontFamily: "inherit",
              transition: "background 0.15s, box-shadow 0.15s",
              boxShadow: "0 2px 10px var(--accent-glow)",
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.background = "var(--accent-d)";
                e.currentTarget.style.boxShadow =
                  "0 4px 18px var(--accent-glow)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--accent)";
              e.currentTarget.style.boxShadow = "0 2px 10px var(--accent-glow)";
            }}
          >
            {isLoading
              ? t("auth.login.form.submitLoading")
              : t("auth.login.form.submit")}
          </button>

          <a
            href="/resetpassword"
            style={{
              display: "block",
              textAlign: "center",
              color: "var(--muted)",
              fontSize: 12,
              textDecoration: "none",
              marginTop: 18,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = "var(--muted2)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--muted)")}
          >
            {t("auth.login.form.forgotPassword")}
          </a>
        </form>
      </div>
    </div>
  );
}

export default Login;
